<script setup lang="ts">
/* =============================================================
   SETTINGS
   =============================================================
   Read-only for now: it shows what the session actually holds, so
   the shape of the profile is visible before there is a backend to
   edit it against.
   ============================================================= */

import { computed } from 'vue'
import Badge from '@/components/ui/Badge.vue'
import Button from '@/components/ui/Button.vue'
import PageHeader from '@/components/ui/PageHeader.vue'
import Panel from '@/components/ui/Panel.vue'
import { appConfig } from '@/config/app.config'
import { useAuth } from '@/lib/auth/useAuth'
import { useDocumentTitle } from '@/lib/composables/useDocumentTitle'

useDocumentTitle('Settings')
const { user, signOut } = useAuth()

const roleLabel = computed(() => {
  const role = user.value?.role
  return appConfig.roles.find((option) => option.value === role)?.label ?? role
})

</script>

<template>
  <template v-if="user">
    <PageHeader title="Settings" description="Your account and organisation details." />

    <div class="stack">
      <Panel title="Profile" description="How you appear across the platform.">
        <template #actions>
          <Button variant="secondary" size="sm" disabled>Edit</Button>
        </template>

        <dl class="list">
          <div class="row">
            <dt>Name</dt>
            <dd>{{ user.name }}</dd>
          </div>
          <div class="row">
            <dt>Email</dt>
            <dd>{{ user.email }}</dd>
          </div>
          <div class="row">
            <dt>Company</dt>
            <dd>{{ user.company }}</dd>
          </div>
          <div class="row">
            <dt>Account type</dt>
            <dd><Badge tone="accent">{{ roleLabel }}</Badge></dd>
          </div>
        </dl>
      </Panel>


      <Panel title="Session">
        <div class="sessionRow">
          <p class="sessionText">Sign out of {{ appConfig.name }} on this device.</p>
          <Button variant="danger" icon="logout" @click="signOut()">Sign out</Button>
        </div>
      </Panel>
    </div>
  </template>
</template>

<style scoped>
.stack {
  display: flex;
  flex-direction: column;
  gap: var(--space-sm);
  max-width: 48rem;
}

.list {
  margin: 0;
}

.row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-sm);
  padding-block: var(--space-2xs);
  font-size: var(--text-sm);
}

.row + .row {
  border-top: var(--border-width) solid var(--color-border);
}

.row dt {
  color: var(--color-text-muted);
}

.row dd {
  margin: 0;
  font-weight: var(--weight-medium);
  color: var(--color-heading);
  text-align: right;
  overflow-wrap: anywhere;
}

.sessionRow {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-sm);
}

.sessionText {
  font-size: var(--text-sm);
  color: var(--color-text-muted);
}
</style>
